import { Box, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { Fragment, useState, useMemo } from 'react';

//Icons for Wire Drivers
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { useNavigate } from 'react-router';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { useSearch } from 'components/Search/SearchContextProvider';
import { useAdvancedSearchContext } from '../../useAdvancedSearchContext';
import { useTranslation } from 'react-i18next';
import { useChannelsSearch } from 'screens/syndication/views/search/channels/ChannelsSearchWithContext';
import { getWireDriverIcons } from 'screens/syndication/utility';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  checkSiteAdminPermission,
  checkSyndicationFullAccessPermission,
  checkSyndicationModifyChannelsPermission,
  PermissionInfo,
} from 'utility/permissions';
import NewActionButton from 'components/Search/components/SearchAdvance/components/Shared/NewActionButton';

export const ChannelsPreSearchContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { driverData } = useChannelsSearch();
  const { validListFilter } = useSearch();
  const navigate = useNavigate();
  const { activeSite, sessionConfig } = useCosmos();
  const { handleQpValueUpdate } = useAdvancedSearchContext();
  const { t } = useTranslation();
  const [anchorElNewBtn, setAnchorElNewBtn] = useState<HTMLElement | null>(null);

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasFullWire } =
    checkSyndicationFullAccessPermission(sessionConfig);

  const { granted: userHasModChannelsWire, name: modifySyndiChannelsPermName } =
    checkSyndicationModifyChannelsPermission(sessionConfig);

  const addBtnState: PermissionInfo = useMemo(() => {
    if (userIsAdmin || userHasFullWire || userHasModChannelsWire) {
      return {
        info: undefined,
        disabled: false,
      };
    }
    return {
      info: t('blox.permissions.msg.required_short', {
        name: modifySyndiChannelsPermName,
      }),
      disabled: true,
    };
  }, [
    t,
    modifySyndiChannelsPermName,
    userHasFullWire,
    userHasModChannelsWire,
    userIsAdmin,
  ]);

  const handleOpenNewMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNewBtn(event.currentTarget);
  };

  const handleCloseNewMenu = () => {
    setAnchorElNewBtn(null);
  };

  // Translation strings...
  const uiNew = t('blox.general.new');

  return (
    <Fragment>
      <NewActionButton
        id="channels-search-new-channel-button"
        aria-label="new-channel-button"
        onClick={handleOpenNewMenu}
        open={Boolean(anchorElNewBtn)}
        disabled={addBtnState.disabled}
        tooltip={addBtnState.info ? addBtnState.info : ''}
      >
        {uiNew}
      </NewActionButton>
      <SelectDropdownInput
        inputLabel={'Driver'}
        staticChoices={(driverData as InputChoice[]) || []}
        value={validListFilter['driver'] as string[]}
        updateValue={(event, values) => handleQpValueUpdate('driver', values)}
        formControlVariant="outlined"
        renderValueDisplay="chips"
        popoverMatchesFormControlWidth
        showFilter={false}
        showFooter={false}
        showChipBox={false}
        showCloseButton={false}
        formControlSx={isMobile ? { flex: 1, minWidth: 0 } : undefined}
        selectSx={isMobile ? { width: '100%' } : { width: 200 }}
      />
      <Menu
        id="channels-search-new-asset-menu"
        data-testid="channels-search-new-asset-menu"
        anchorEl={anchorElNewBtn}
        open={Boolean(anchorElNewBtn)}
        onClose={handleCloseNewMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
            },
          },
          list: {
            dense: true,
          },
        }}
      >
        {driverData.map((driver) => {
          return (
            <MenuItem
              key={driver.value}
              onClick={() => {
                handleCloseNewMenu();
                navigate(`/${activeSite}/feeds/channels/create/${driver.value}`);
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getWireDriverIcons(driver.value, 14)}
                {driver.label}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
};
